import type { PlayerLine, RosterSide } from '../api';

const PITCH_W = 240;
const PITCH_H = 320;

// Standard formation maps: rows (back to front) of player counts.
const FORMATIONS: Record<string, number[]> = {
  '4-4-2': [1, 4, 4, 2],
  '4-3-3': [1, 4, 3, 3],
  '4-2-3-1': [1, 4, 2, 3, 1],
  '3-5-2': [1, 3, 5, 2],
  '3-4-3': [1, 3, 4, 3],
  '5-3-2': [1, 5, 3, 2],
  '5-4-1': [1, 5, 4, 1],
  '4-5-1': [1, 4, 5, 1],
  '4-1-4-1': [1, 4, 1, 4, 1],
  '4-3-2-1': [1, 4, 3, 2, 1],
  '3-4-2-1': [1, 3, 4, 2, 1],
  '4-4-1-1': [1, 4, 4, 1, 1],
};

function rowsForFormation(f: string | null, n: number): number[] {
  if (f && FORMATIONS[f]) return FORMATIONS[f];
  // Fallback: try parsing "4-3-3"
  if (f) {
    const parts = f.split('-').map((s) => parseInt(s, 10)).filter((x) => Number.isFinite(x) && x > 0);
    if (parts.length >= 2 && parts.reduce((a, b) => a + b, 0) === 10) return [1, ...parts];
  }
  // Last resort: spread evenly with GK at back
  return n === 11 ? [1, 4, 4, 2] : [1, ...Array(Math.max(1, n - 1)).fill(1)];
}

export default function PitchFormation({ roster, flip }: { roster: RosterSide; flip?: boolean }) {
  const starters = roster.starters;
  if (starters.length === 0) return null;
  const rows = rowsForFormation(roster.formation, starters.length);

  // Distribute starters into rows by formationPlace order (which matches back→front in ESPN).
  // If counts don't match, we fall back to even distribution from input order.
  const total = rows.reduce((a, b) => a + b, 0);
  const usable = total === starters.length ? rows : [1, ...Array(starters.length - 1).fill(1)];

  const placed: { player: PlayerLine; row: number; col: number; rowCount: number }[] = [];
  let idx = 0;
  for (let r = 0; r < usable.length; r++) {
    for (let c = 0; c < usable[r]; c++) {
      if (idx >= starters.length) break;
      placed.push({ player: starters[idx], row: r, col: c, rowCount: usable[r] });
      idx += 1;
    }
  }

  const rowCount = usable.length;
  const rowGap = PITCH_H / (rowCount + 1);
  const ySign = flip ? -1 : 1;

  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-dim mb-1">
        {roster.teamName || roster.teamCode}
        {roster.formation && <span className="ml-2 font-mono">{roster.formation}</span>}
      </div>
      <svg viewBox={`0 0 ${PITCH_W} ${PITCH_H}`} className="w-full h-auto block rounded-lg" style={{ background: 'linear-gradient(180deg, #052e16, #14532d)' }}>
        <rect x="2" y="2" width={PITCH_W - 4} height={PITCH_H - 4} fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
        <line x1="2" y1={PITCH_H / 2} x2={PITCH_W - 2} y2={PITCH_H / 2} stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
        <circle cx={PITCH_W / 2} cy={PITCH_H / 2} r="28" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
        {placed.map(({ player, row, col, rowCount: rc }) => {
          const yRow = (row + 1) * rowGap;
          const y = flip ? PITCH_H - yRow : yRow;
          void ySign;
          const colGap = PITCH_W / (rc + 1);
          const x = (col + 1) * colGap;
          const hasGoal = player.stats.goals > 0;
          const hasRed = player.stats.redCards > 0;
          const hasYellow = player.stats.yellowCards > 0 && !hasRed;
          const ring = hasRed ? '#ef4444' : hasYellow ? '#eab308' : hasGoal ? '#22c55e' : 'rgba(255,255,255,0.4)';
          const fill = hasGoal ? 'rgba(34,197,94,0.25)' : 'rgba(0,0,0,0.55)';
          return (
            <g key={player.id ?? player.name}>
              <circle cx={x} cy={y} r="12" fill={fill} stroke={ring} strokeWidth="1.5" />
              <text x={x} y={y + 3.5} textAnchor="middle" fontSize="9" fontFamily="ui-monospace, monospace" fill="#fff">
                {player.jersey ?? '·'}
              </text>
              <text x={x} y={y + 24} textAnchor="middle" fontSize="7" fill="rgba(255,255,255,0.85)">
                {short(player.name)}
              </text>
              {hasGoal && <text x={x + 10} y={y - 8} fontSize="9">⚽</text>}
              {hasYellow && <text x={x + 10} y={y - 8} fontSize="9">🟨</text>}
              {hasRed && <text x={x + 10} y={y - 8} fontSize="9">🟥</text>}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function short(name: string): string {
  const parts = name.split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 10);
  const last = parts[parts.length - 1];
  return last.length > 10 ? last.slice(0, 10) : last;
}
